import axios from 'axios';
import { Dispatch } from 'redux';
import { City, Country, CityData, CountryInfo, 
          CityYoutube, CityCmsEntry, CityCmsData } from "@/data/types";
import api from './api';

// Action Types
const GET_CITIES = 'GET_CITIES';
const GET_FEATURED_CITIES = 'GET FEATURED CITIES';
const GET_CITY_DATA_REQUEST = 'GET_CITY_DATA_REQUEST';
const GET_CITY_DATA_SUCCESS = 'GET_CITY_DATA_SUCCESS';
const GET_CITY_DATA_FAILURE = 'GET_CITY_DATA_FAILURE';

const GET_CITY_CMS_DATA = 'GET_CITY_CMS_DATA';
const GET_COUNTRIES_WITH_CITIES = 'GET_COUNTRIES_WITH_CITIES'; // New action type

// Action Interfaces
interface GetCitiesAction {
  type: typeof GET_CITIES;
  payload: City[];
}

interface GetFeaturedCitiesAction {
  type: typeof GET_FEATURED_CITIES;
  payload: City[];
}

interface GetCityDataRequestAction {
  type: typeof GET_CITY_DATA_REQUEST;
}

interface GetCityDataSuccessAction {
  type: typeof GET_CITY_DATA_SUCCESS;
  payload: CityData;
}

interface GetCityDataFailureAction {
  type: typeof GET_CITY_DATA_FAILURE;
}

interface GetCityCmsDataAction {
  type: typeof GET_CITY_CMS_DATA;
  payload: CityCmsData[];
}

interface GetCountriesWithCitiesAction { // New interface for countries with cities
  type: typeof GET_COUNTRIES_WITH_CITIES;
  payload: Country[];
}

// Async Action Creator
export const getFeaturedCities = (activated?: boolean, countryId?: string, featured?: boolean, language?: string) => async (dispatch: Dispatch<GetFeaturedCitiesAction>) => {
  try {
    // Construct query parameters based on 'activated' and 'countryId' arguments
    const params: { activated?: boolean; countryId?: string; featured?: boolean; language?: string } = {};

    if (activated !== undefined) {
      params.activated = activated;
    }

    if (countryId !== undefined) {
      params.countryId = countryId;
    }

    if (featured !== undefined) {
      params.featured = featured;
    }

    if (language !== undefined) {
      params.language = language;
    }

    const response = await api.get(`/api/featuredCities`, { params });

    if (response.data) {
      dispatch({
        type: GET_FEATURED_CITIES,
        payload: response.data.sort((a: City, b: City) => a.order - b.order), // Sorting by 'order'
      });
    } else {
      // Handle the case when data is not available
      // You can dispatch another action or handle it as needed
    }
  } catch (error) {
    console.error('Error fetching continents:', error);
    // You can dispatch an error action or handle it as needed
  }
};

export const getCities = (activated?: boolean, 
                          countryId?: string, 
                          featured?: boolean, 
                          language?: string) => async (dispatch: Dispatch<GetCitiesAction>) => {
  try {
    // Construct query parameters based on 'activated' and 'countryId' arguments
    const params: { activated?: boolean; countryId?: string; featured?: boolean; language?: string } = {};

    if (activated !== undefined) {
      params.activated = activated;
    }

    if (countryId !== undefined) {
      params.countryId = countryId;
    }

    if (featured !== undefined) {
      params.featured = featured;
    }

    if (language !== undefined) {
      params.language = language;
    }

    const response = await axios.get(`${process.env.NEXT_PUBLIC_SOCKET}/api/cities`, { params });

    if (response.data) {
      dispatch({
        type: GET_CITIES,
        payload: response.data.sort((a: City, b: City) => a.order - b.order), // Sorting by 'order'
      });
    } else {
      // Handle the case when data is not available
      // You can dispatch another action or handle it as needed
    }
  } catch (error) {
    console.error('Error fetching continents:', error);
    // You can dispatch an error action or handle it as needed
  }
};

// Sync setter for server-side hydration
export const setCityData = (payload: CityData) => ({
  type: GET_CITY_DATA_SUCCESS as typeof GET_CITY_DATA_SUCCESS,
  payload,
});

// Sync setter for featured cities — lets the home server component prefill
// state.cities.featured before the client thunk fires, so first paint already
// has the homepage carousel data.
export const setFeaturedCities = (payload: City[]) => ({
  type: GET_FEATURED_CITIES as typeof GET_FEATURED_CITIES,
  payload,
});

export const getCityData = (slug: string, language: string) => async (dispatch: Dispatch) => {
  dispatch({ type: GET_CITY_DATA_REQUEST });

  try {
    const response = await axios.get(`${process.env.NEXT_PUBLIC_SOCKET}/api/city/data`, {
      params: { slug, language }
    });

    dispatch({
      type: GET_CITY_DATA_SUCCESS,
      payload: response.data,
    });
  } catch (error) {
    console.error('Error fetching city data:', error);
    dispatch({ type: GET_CITY_DATA_FAILURE });
  }
};

export const getCityCmsData = (content_cms_entry__: string, language: string) => async (dispatch: Dispatch<GetCityCmsDataAction>) => {
  try {
    const response = await axios.get(`${process.env.NEXT_PUBLIC_SOCKET}/api/city/cms/data`, {
      params: { content_cms_entry__, language }
    });

    // If the response is successful, dispatch the data to the store
    dispatch({
      type: GET_CITY_CMS_DATA,
      payload: response.data,
    });
  } catch (error) {
    if (axios.isAxiosError(error) && error.response?.status === 404 && language !== 'en_US') {
      // If there's a 404 error and the language is not already 'en_US', try again with 'en_US'
      try {
        const fallbackResponse = await axios.get(`${process.env.NEXT_PUBLIC_SOCKET}/api/city/cms/data`, {
          params: { content_cms_entry__, language: 'en_US' }
        });

        dispatch({
          type: GET_CITY_CMS_DATA,
          payload: fallbackResponse.data,
        });
      } catch (fallbackError) {
        console.error('Error fetching city CMS data with fallback language:', fallbackError);
        // Handle further error (maybe dispatch an error action)
      }
    } else {
      // Handle other errors or a 404 when the language is already 'en_US'
      console.error('Error fetching city CMS data:', error);
      // Handle error (maybe dispatch an error action)
    }
  }
};

// Async Action Creator for countries with cities
export const getCountriesWithCities = () => async (dispatch: Dispatch<GetCountriesWithCitiesAction>) => {
  try {
    const response = await axios.get(`${process.env.NEXT_PUBLIC_SOCKET}/api/countries-with-cities`);
    if (response.data) {
      dispatch({
        type: GET_COUNTRIES_WITH_CITIES,
        payload: response.data,
      });
    } else {
      // Handle the case when data is not available
    }
  } catch (error) {
    console.error('Error fetching countries with cities:', error);
    // Error handling
  }
};


// State Type
interface CityState {
  cities: City[];
  featured: City[];
  currentCityData: CityData | null;
  cityCmsData: CityCmsData[] | null;
  loadingCityData: boolean;
}

const initialState: CityState = {
  cities: [],
  featured: [],
  currentCityData: null,
  cityCmsData: null,
  loadingCityData: false,
};


// Reducer
const cityReducer = (
  state: CityState = initialState,
  action: 
    | GetCitiesAction
    | GetFeaturedCitiesAction
    | GetCityDataRequestAction
    | GetCityDataSuccessAction
    | GetCityDataFailureAction
    | GetCityCmsDataAction
    | GetCountriesWithCitiesAction
) => {
  switch (action.type) {
    case GET_CITIES: {
      return {
        ...state,
        cities: action.payload,
      };
    }
    case GET_FEATURED_CITIES: {
      return {
        ...state,
        featured: action.payload,
      };
    }
    case GET_CITY_DATA_REQUEST:
      return {
        ...state,
        loadingCityData: true,
      };
    case GET_CITY_DATA_SUCCESS:
      return {
        ...state,
        loadingCityData: false,
        currentCityData: action.payload,
      };
    case GET_CITY_DATA_FAILURE:
      return {
        ...state,
        loadingCityData: false,
      };
    case GET_CITY_CMS_DATA: {
      return {
        ...state,
        cityCmsData: action.payload,
      };
    }
    case GET_COUNTRIES_WITH_CITIES: { // Handling the new action
      // Update your state as needed to include countries with cities
      // ...
    }
    default: {
      return state;
    }
  }
};

export default cityReducer;